import React, {useEffect} from 'react';
import Constants from "../../../utils/constants";
import './CampaignMap.css'
import '../../../../node_modules/leaflet/dist/leaflet.css'
import '../../../../node_modules/leaflet-draw/dist/leaflet.draw.css'
import {helper} from "../../../utils/helper";
import {FeatureGroup, MapContainer, Marker, Tooltip} from 'react-leaflet';
import {EditControl} from "react-leaflet-draw";
import {divIcon, Icon} from "leaflet";
import Button from "@mui/material/Button";
import L from 'leaflet';
import '../../../utils/libs/leafletPointInPolygon'
import CustomTileLayer from "../layout/map/CustomTileLayer.js";

/**
 * Sémantique de sélection : le polygone est un "pinceau d'ajout" (union avec la
 * sélection courante), le clic sur un marqueur fait un toggle du point, et
 * "Tout retirer" vide la sélection. Supprimer un polygone n'enlève donc PAS les
 * conteneurs qu'il avait ajoutés : le tracé est un outil, pas un état.
 */
function CampaignMap({patrimoine, conteneurs, selectedConteneurs, onConteneursSelected}) {
    const [conteneursData, setConteneurData] = React.useState(null)
    // State (et non ref) : l'instance n'existe qu'après le mount de MapContainer,
    // le state garantit que l'effet de cadrage rejoue quand elle arrive
    const [map, setMap] = React.useState(null);

    const selectedIds = new Set((selectedConteneurs || []).map(c => String(c.id)));

    // Cadrage automatique sur les marqueurs (remplace l'ancien centre moyenné + zoom fixe)
    useEffect(() => {
        if (!map) return;

        if (conteneurs && conteneurs.length > 0) {
            const bounds = L.latLngBounds(
                conteneurs.map(c => [parseFloat(c.latitude), parseFloat(c.longitude)])
            );
            map.fitBounds(bounds, {padding: [40, 40], maxZoom: 16});
        } else if (patrimoine.isUnipatrimoine) {
            map.setView([patrimoine.latitude, patrimoine.longitude], 15);
        }
    }, [map, conteneurs, patrimoine]);

    // Groupement des conteneurs par coordonnées identiques (même adresse)
    useEffect(() => {
        if (conteneurs) {
            let data = {}
            let len = conteneurs.length;
            while (len--) {
                let key = conteneurs[len].latitude + "/" + conteneurs[len].longitude
                if (data[key]) {
                    data[key].push(conteneurs[len])
                } else {
                    data[key] = [conteneurs[len]]
                }
            }

            setConteneurData(data)
        }
    }, [conteneurs])

    const getMarkersInsidePolygon = (polygonPoints) => {
        if (!polygonPoints || polygonPoints.length === 0) {
            return [];
        }
        let leafletPolygon = L.polygon(polygonPoints);

        return conteneurs.filter(c => {
            let point = L.latLng(parseFloat(c.latitude), parseFloat(c.longitude));
            return leafletPolygon.contains(point);
        });
    };

    // Union : ajoute à la sélection courante sans doublons
    const addToSelection = (toAdd) => {
        const current = selectedConteneurs || [];
        const merged = [
            ...current,
            ...toAdd.filter(c => !selectedIds.has(String(c.id)))
        ];
        if (merged.length !== current.length) {
            onConteneursSelected(merged);
        }
    };

    // Toggle d'un groupe de conteneurs (un marqueur = tous les conteneurs de l'adresse)
    const toggleGroup = (group) => {
        const groupIds = group.map(c => String(c.id));
        const allSelected = group.every(c => selectedIds.has(String(c.id)));

        if (allSelected) {
            onConteneursSelected(
                (selectedConteneurs || []).filter(c => !groupIds.includes(String(c.id)))
            );
        } else {
            addToSelection(group);
        }
    };

    const onPolygonCreate = (e) => {
        const {layerType, layer} = e;
        if (layerType === 'polygon') {
            addToSelection(getMarkersInsidePolygon(layer.getLatLngs()));
        }
    };

    const onPolygonEdited = (e) => {
        const {layers} = e;
        layers.eachLayer((layer) => {
            if (layer instanceof L.Polygon) {
                addToSelection(getMarkersInsidePolygon(layer.getLatLngs()));
            }
        });
    };

    const onAddAll = () => {
        onConteneursSelected(conteneurs)
    }

    const onRemoveAll = () => {
        onConteneursSelected([])
    }

    function getIcon(isSelected, count = 1) {
        // Vert = sélectionné, gris = non sélectionné (mêmes assets que le suivi de lavage)
        let imgSrc = helper.getConteneurStatusIcon(
            isSelected ? Constants.CONTENEURS_STATUS_DONE : Constants.CONTENEURS_STATUS_TODO
        );
        return divIcon({
            className: 'my-div-icon',
            html: `<img class="marker-image" src="${imgSrc}"/>` +
                `<span class="marker-number-div">${count}</span>`,
            iconSize: [(56 / 1.5), (76 / 1.5)]
        });
    }

    return (
        <>
            <MapContainer
                ref={setMap}
                center={[46.6, 2.4]}
                zoom={6}
                style={{height: "85vh", width: "100%"}}
            >
                <CustomTileLayer
                    maxZoom={19}
                />
                {
                    conteneursData &&
                    Object.entries(conteneursData).map(([key, group]) => {
                        const groupSelected = group.every(c => selectedIds.has(String(c.id)));

                        // Un seul marqueur par adresse : les conteneurs superposés étaient
                        // empilés au même point, indissociables et coûteux à rendre
                        return (
                            <Marker
                                key={key}
                                position={[parseFloat(group[0].latitude), parseFloat(group[0].longitude)]}
                                icon={getIcon(groupSelected, group.length)}
                                eventHandlers={{
                                    click: () => toggleGroup(group),
                                }}
                            >
                                <Tooltip direction="top" offset={[0, -40]}>
                                    <div>
                                        <strong>{group[0].adresse}</strong>
                                        {group.length > 1 && <div>{group.length} conteneurs</div>}
                                        <div style={{fontStyle: 'italic'}}>
                                            {groupSelected ? "Cliquer pour retirer" : "Cliquer pour ajouter"}
                                        </div>
                                    </div>
                                </Tooltip>
                            </Marker>
                        )
                    })
                }

                {patrimoine.isUnipatrimoine &&
                    <Marker
                        icon={new Icon({
                            iconUrl: helper.getConteneurStatusIcon(),
                            iconSize: [(56 / 1.5), (76 / 1.5)],
                        })}
                        position={[patrimoine.latitude, patrimoine.longitude]}/>
                }
                <FeatureGroup>
                    <EditControl
                        position='topright'
                        onCreated={onPolygonCreate}
                        onEdited={onPolygonEdited}
                        draw={{
                            rectangle: false,
                            circle: false,
                            polyline: false,
                            circlemarker: false,
                            marker: false,
                        }}
                    />
                </FeatureGroup>
                {!patrimoine.isUnipatrimoine && (
                    <div className={"campaignMapButtons-wrapper"}>
                        <Button variant={"contained"} color={"primary"} onClick={onAddAll}>
                            Ajouter tout
                        </Button>
                        <Button variant={"outlined"} color={"primary"} onClick={onRemoveAll}
                                sx={{marginLeft: 1, backgroundColor: 'white'}}>
                            Tout retirer
                        </Button>
                    </div>
                )}
            </MapContainer>
        </>
    )
}


export default CampaignMap;
